import { useState } from 'react';
import { motion } from 'framer-motion';
import { Activity, Server, Database, Cpu, ClipboardCheck, Loader2 } from 'lucide-react';
import { GlassCard } from '../components/ui/GlassCard';
import { SystemPerformance } from '../components/kpi/SystemPerformance';
import { LatencyDashboard } from '../components/kpi/LatencyDashboard';
import { MetricsStream } from '../components/kpi/MetricsStream';
import { useMetricsData } from '../hooks/useMetricsData';
import { pageTransition, staggerContainer, fadeUpItem } from '../hooks/useAnimation';

const SERVICES = [
  { name: 'Gateway', port: 8001, healthy: true },
  { name: 'MCP Service', port: 8002, healthy: true },
  { name: 'Audio / STT', port: 8003, healthy: true },
  { name: 'TTS', port: 8004, healthy: true },
  { name: 'Agent System', port: 8005, healthy: true },
  { name: 'Recording', port: 8006, healthy: true },
  { name: 'Metrics', port: 8007, healthy: true },
  { name: 'Observability', port: 8008, healthy: true },
];

function EvalPanel() {
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState<any | null>(null);
  const [err, setErr] = useState('');

  const run = async () => {
    setRunning(true); setErr('');
    try {
      const r = await fetch('/api/agents/eval', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setReport(await r.json());
    } catch (e: any) { setErr(e.message || 'Evaluation failed'); }
    finally { setRunning(false); }
  };

  return (
    <GlassCard padding="md">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-5 h-5 text-vokentra" />
          <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider">AI Evaluation Suite</h2>
        </div>
        <button onClick={run} disabled={running}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-vokentra/10 border border-vokentra/20 text-vokentra text-sm font-medium hover:bg-vokentra/20 disabled:opacity-60">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />}
          {running ? 'Running…' : 'Run evaluation'}
        </button>
      </div>

      {err && <p className="text-sm text-danger mb-3">{err}</p>}
      {!report && !err && (
        <p className="text-sm text-text-muted">
          Runs 9 named tests (intent routing, billing, network outage, SIM-swap security, roaming, human
          escalation, compliance guardrails, post-call summary, no-hallucination) against the live pipeline.
        </p>
      )}

      {report && (
        <div className="space-y-3">
          <div className="flex items-center gap-4">
            <div className="text-3xl font-bold text-text-primary">{Math.round(report.overall_score * 100)}%</div>
            <div className="text-sm text-text-muted">
              <span className="text-success font-medium">{report.passed} passed</span>
              {report.failed > 0 && <span className="text-danger font-medium"> · {report.failed} failed</span>}
              <span> · {report.total} tests · {report.token_usage?.total_tokens ?? 0} tokens</span>
            </div>
          </div>
          <div className="space-y-1.5">
            {report.results.map((t: any) => (
              <div key={t.name} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-primary font-mono">{t.name}</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${t.passed ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}>
                    {t.passed ? 'PASS' : 'FAIL'} · {Math.round(t.score * 100)}%
                  </span>
                </div>
                {!t.passed && t.checks.filter((c: any) => !c.ok).map((c: any, i: number) => (
                  <p key={i} className="text-[11px] text-danger/80 mt-1">✗ {c.name}{c.detail ? ` — ${c.detail}` : ''}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </GlassCard>
  );
}

export function System() {
  const { sttLatency, agentLatency, ttsLatency, callEvents, system } = useMetricsData(2000);

  return (
    <motion.div variants={pageTransition} initial="initial" animate="animate" exit="exit" className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
            <Server className="w-6 h-6 text-vokentra" />
            System Health
          </h1>
          <p className="text-sm text-text-muted mt-1">Service status, latency, and infrastructure metrics</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-success/5 border border-success/10">
          <span className="w-2 h-2 rounded-full bg-success animate-pulse-fast" />
          <span className="text-xs text-success font-medium">All systems operational</span>
        </div>
      </div>

      {/* Service grid */}
      <motion.div variants={staggerContainer(0.05)} initial="hidden" animate="visible" className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {SERVICES.map((s) => (
          <motion.div key={s.name} variants={fadeUpItem}>
            <GlassCard padding="md">
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-primary">{s.name}</span>
                <span className={`w-2.5 h-2.5 rounded-full ${s.healthy ? 'bg-success' : 'bg-error'}`} />
              </div>
              <p className="text-[11px] text-text-muted mt-1 font-mono">:{s.port}</p>
            </GlassCard>
          </motion.div>
        ))}
      </motion.div>

      {/* Infra tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <GlassCard padding="md">
          <div className="flex items-center gap-2 text-text-muted text-xs"><Cpu className="w-4 h-4" /> Concurrent calls</div>
          <p className="text-2xl font-bold text-text-primary tabular-nums mt-1">
            {system?.concurrentCalls ?? 0}<span className="text-sm text-text-muted"> / {system?.maxCapacity ?? 0}</span>
          </p>
        </GlassCard>
        <GlassCard padding="md">
          <div className="flex items-center gap-2 text-text-muted text-xs"><Database className="w-4 h-4" /> Data stores</div>
          <p className="text-sm text-text-secondary mt-2">PostgreSQL · Redis · MinIO</p>
        </GlassCard>
        <GlassCard padding="md">
          <div className="flex items-center gap-2 text-text-muted text-xs"><Activity className="w-4 h-4" /> Success rate</div>
          <p className="text-2xl font-bold text-success tabular-nums mt-1">{(system?.successRate ?? 0).toFixed(1)}%</p>
        </GlassCard>
      </div>

      {/* Latency */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-success" />
          <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider">Pipeline Latency</h2>
          <div className="flex-1 h-px bg-white/[0.06] ml-2" />
        </div>
        <LatencyDashboard sttLatency={sttLatency} agentLatency={agentLatency} ttsLatency={ttsLatency} />
      </div>

      {/* Performance + stream */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <SystemPerformance system={system} />
        </div>
        <div className="xl:col-span-1">
          <MetricsStream events={callEvents} className="h-full" />
        </div>
      </div>

      {/* Evaluation suite */}
      <EvalPanel />
    </motion.div>
  );
}

export default System;
